import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSubscription } from "../../context/SubscriptionContext";
import { AFTER_LOGIN_PATH, isProfileIncomplete } from "../../features/auth/profileCompletion";
import {
  canParentSessionAccessProtectedPath,
  isParentSessionMode,
  PARENT_DEFAULT_REDIRECT_PATH,
  readParentPostAuthRedirect,
} from "../../features/parentFlow/parentFlow";
import {
  getAccessibleBasicYearId,
  getCurrentUnlockedYearId,
  isPaidBasicUser,
} from "../../utils/basicPlanAccess";

const FullScreenState = ({ title, message, onRetry, showRetry }) => (
  <div className="min-h-screen flex items-center justify-center bg-blue-50 px-4">
    <div className="bg-white rounded-xl shadow-md p-6 max-w-md w-full text-center">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
      <p className="text-gray-600">{message}</p>
      {showRetry && (
        <button
          onClick={onRetry}
          className="mt-4 px-5 py-2 rounded-lg bg-blue-700 text-white font-semibold hover:bg-blue-800"
        >
          Retry
        </button>
      )}
    </div>
  </div>
);

export const AuthGuard = ({ element }) => {
  const location = useLocation();
  const { user, token } = useSelector((state) => state.user);

  if (!user || !token) {
    return <Navigate to="/login" replace />;
  }

  if (
    isParentSessionMode() &&
    !canParentSessionAccessProtectedPath(location.pathname)
  ) {
    return (
      <Navigate
        to={readParentPostAuthRedirect(PARENT_DEFAULT_REDIRECT_PATH)}
        replace
      />
    );
  }

  if (
    isProfileIncomplete(user) &&
    location.pathname !== AFTER_LOGIN_PATH
  ) {
    return <Navigate to={AFTER_LOGIN_PATH} replace />;
  }

  return element;
};

export const SubscriptionRouteGuard = ({
  element,
  allowTrialOrActive = false,
  requiredPlanType = "",
  restrictBasicToCurrentYear = false,
}) => {
  const location = useLocation();
  const { user, token } = useSelector((state) => state.user);
  const {
    subscription,
    hasAccess,
    checkError,
    hasCheckedOnce,
    checkSubscription,
    normalizedPlanType,
  } = useSubscription();
  const status = subscription?.subscription_status;
  const hasTrialOrActiveStatus = status === "trialing" || status === "active";
  const canAccess = allowTrialOrActive ? hasTrialOrActiveStatus || hasAccess : hasAccess;
  const hasRequiredPlan =
    !requiredPlanType || normalizedPlanType === requiredPlanType;
  const requestedYearId = Number(
    location.state?.yearId ||
      new URLSearchParams(location.search).get("yearId") ||
      0
  );
  const isBasicPlanUser =
    normalizedPlanType === "basic" || isPaidBasicUser(user);
  const currentUnlockedYearId = isBasicPlanUser
    ? getAccessibleBasicYearId(user)
    : getCurrentUnlockedYearId(user);
  const isBasicCurrentYearRestricted =
    restrictBasicToCurrentYear &&
    isBasicPlanUser &&
    canAccess &&
    currentUnlockedYearId > 0;
  const isCompletionFlow = Number(location.state?.yearCompletionStatus) === 1;

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (
    isParentSessionMode() &&
    !canParentSessionAccessProtectedPath(location.pathname)
  ) {
    return (
      <Navigate
        to={readParentPostAuthRedirect(PARENT_DEFAULT_REDIRECT_PATH)}
        replace
      />
    );
  }

  if (isProfileIncomplete(user)) {
    return <Navigate to={AFTER_LOGIN_PATH} replace />;
  }

  if (!hasCheckedOnce) {
    return element;
  }

  if (checkError) {
    return (
      <FullScreenState
        title="Unable to verify subscription"
        message="Please retry. Access is granted only after subscription status is confirmed."
        onRetry={() => checkSubscription().catch(() => null)}
        showRetry
      />
    );
  }

  if (!canAccess) {
    const subscriptionPath = `/subscription${location.search || ""}`;
    return (
      <Navigate
        to={subscriptionPath}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!hasRequiredPlan) {
    return <Navigate to="/yearsdashboard" replace />;
  }

  if (
    isBasicCurrentYearRestricted &&
    requestedYearId > 0 &&
    requestedYearId !== currentUnlockedYearId &&
    !isCompletionFlow
  ) {
    return <Navigate to="/yearsdashboard" replace />;
  }

  return element;
};
